import { DollarSign, MapPin, Phone, Mail, Clock, Shield, Users, CheckCircle, Star, Award, CreditCard, Zap, Building, FileText, Calculator, TrendingUp, BookOpen, HelpCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const HowItWorks = () => {
  const steps = [
    {
      step: "1",
      title: "Complete Application",
      description: "Fill out our simple online application form with your basic information, employment details, and banking information. The process takes just 3-5 minutes.",
      icon: <FileText className="w-12 h-12 text-green-600" />
    },
    {
      step: "2", 
      title: "Get Instant Decision",
      description: "Our advanced system reviews your application instantly. You'll receive an approval decision within minutes, not hours or days.",
      icon: <Zap className="w-12 h-12 text-blue-600" />
    },
    {
      step: "3",
      title: "Review Terms",
      description: "If approved, review your loan terms including amount, interest rate, and repayment schedule. Everything is transparent with no hidden fees.",
      icon: <CheckCircle className="w-12 h-12 text-purple-600" />
    },
    {
      step: "4",
      title: "Receive Funds",
      description: "Accept your loan terms and receive funds directly deposited into your bank account. Same-day funding available for qualified applicants.",
      icon: <DollarSign className="w-12 h-12 text-orange-600" />
    }
  ];

  const requirements = [
    {
      title: "Age Requirement",
      description: "Must be at least 18 years old",
      icon: <Users className="w-6 h-6 text-green-600" />
    },
    {
      title: "Income Verification",
      description: "Steady source of income (employment, benefits, etc.)",
      icon: <TrendingUp className="w-6 h-6 text-blue-600" />
    },
    {
      title: "Bank Account",
      description: "Active checking account in your name",
      icon: <Building className="w-6 h-6 text-purple-600" />
    },
    {
      title: "Contact Information",
      description: "Valid phone number and email address",
      icon: <Phone className="w-6 h-6 text-orange-600" />
    }
  ];

  const faqs = [
    {
      question: "How quickly can I get my loan?",
      answer: "Most approved loans are funded the same day. For applications submitted before 2 PM CT on business days, funds are typically available within hours."
    },
    {
      question: "Do you check my credit score?",
      answer: "We may perform a soft credit check that doesn't affect your credit score. Our approval is primarily based on your income and ability to repay."
    },
    {
      question: "What if I have bad credit?",
      answer: "Bad credit is not automatically disqualifying. We focus on your current financial situation and ability to repay rather than past credit issues."
    },
    {
      question: "Are there any hidden fees?",
      answer: "No hidden fees. All costs are clearly disclosed upfront before you accept your loan. We believe in complete transparency."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-lg">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="https://quickloanpro.com/storage/2023/11/Payday-Loans-Louisiana-Logo.png" 
                alt="QuickLoanPro Logo"
                className="w-16 h-16 object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">QuickLoanPro</h1>
                <p className="text-gray-600">Your Trusted Financial Partner</p>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-4">
              <Phone className="w-5 h-5 text-green-600" />
              <span className="text-gray-700">+1 (504) 438-1753</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-green-600 text-white py-3">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="hover:text-green-200 transition-colors">Home</Link>
            <Link to="/payday-loans" className="hover:text-green-200 transition-colors">Payday Loans</Link>
            <Link to="/personal-loans" className="hover:text-green-200 transition-colors">Personal Loans</Link>
            <Link to="/how-it-works" className="font-semibold">How It Works</Link>
            <Link to="/faq" className="hover:text-green-200 transition-colors">FAQ</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            How QuickLoanPro Works
          </h2>
          <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
            Getting a loan from QuickLoanPro is simple, fast, and transparent. Follow our 4-step process to get the funds you need today.
          </p>
          <Button 
            size="lg" 
            className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
            onClick={() => window.open('https://quickloanpro.com/', '_blank')}
          >
            Start Your Application
          </Button>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Simple 4-Step Process
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <Card key={index} className="text-center hover:shadow-xl transition-shadow duration-300 border-2 border-green-200">
                <CardContent className="p-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="text-2xl font-bold text-green-600">{step.step}</span>
                  </div>
                  <div className="flex justify-center mb-4">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-4">{step.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Basic Requirements
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {requirements.map((req, index) => (
              <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-4">
                    {req.icon}
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{req.title}</h3>
                  <p className="text-gray-600 text-sm">{req.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-gray-700 mb-6">
              Meet these basic requirements? You're likely to qualify for a QuickLoanPro loan.
            </p>
            <Button 
              size="lg" 
              className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-4"
              onClick={() => window.open('https://quickloanpro.com/', '_blank')}
            >
              Check Your Eligibility
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gradient-to-r from-green-50 to-blue-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Frequently Asked Questions
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {faqs.map((faq, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-3 text-green-800">{faq.question}</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/faq">
              <Button variant="outline" size="lg" className="border-green-600 text-green-600 hover:bg-green-50">
                View All FAQs
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Internal Links Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Explore Our Services
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <DollarSign className="w-8 h-8 text-green-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Payday Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Quick cash until payday</p>
                <Link to="/payday-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <CreditCard className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Personal Loans</h3>
                <p className="text-sm text-gray-600 mb-4">Larger amounts, flexible terms</p>
                <Link to="/personal-loans">
                  <Button variant="outline" size="sm">Learn More</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <Calculator className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">Loan Calculator</h3>
                <p className="text-sm text-gray-600 mb-4">Calculate payments</p>
                <Link to="/loan-calculator">
                  <Button variant="outline" size="sm">Calculate</Button>
                </Link>
              </CardContent>
            </Card>
            
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <HelpCircle className="w-8 h-8 text-orange-600 mx-auto mb-3" />
                <h3 className="font-semibold mb-2">FAQ</h3>
                <p className="text-sm text-gray-600 mb-4">Get answers</p>
                <Link to="/faq">
                  <Button variant="outline" size="sm">View FAQ</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-blue-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Now that you know how it works, apply for your loan today and get funds as soon as today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl px-8 py-4"
              onClick={() => window.open('https://quickloanpro.com/', '_blank')}
            >
              Apply Now
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-green-600 font-bold text-xl px-8 py-4"
            >
              Call +1 (504) 438-1753
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">QuickLoanPro</h3>
              <p className="text-gray-400 mb-4">
                Your trusted partner for loans and financial assistance across Louisiana.
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (504) 438-1753</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@quickloanpro.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>4901 Bloomfield St, New Orleans, LA 70121</span>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Loan Services</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/payday-loans" className="hover:text-white">Payday Loans</Link></li>
                <li><Link to="/personal-loans" className="hover:text-white">Personal Loans</Link></li>
                <li><Link to="/installment-loans" className="hover:text-white">Installment Loans</Link></li>
                <li><Link to="/cash-advances" className="hover:text-white">Cash Advances</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/loan-calculator" className="hover:text-white">Loan Calculator</Link></li>
                <li><Link to="/faq" className="hover:text-white">FAQ</Link></li>
                <li><Link to="/responsible-lending" className="hover:text-white">Responsible Lending</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 QuickLoanPro. All rights reserved.</p>
            <p className="mt-2">Licensed financial service provider committed to responsible lending practices.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorks;